<script setup>
import { computed, nextTick, onBeforeUnmount, onMounted, ref, watch } from "vue";
import { useRoute } from "vue-router";
import BreadCrumb from '@/views/Layout/BreadCrumb.vue'
import SearchBar from '@/components/SearchBar.vue'
import TimeAgo from "@/components/TimeAgo.vue";
import * as Icon from "@heroicons/vue/24/solid";
import { useAuthStore } from "@/stores/AuthStore";
import { useBreadcrumbStore } from "@/stores/BreadcrumbStore";
import { recordRecentNavigation, useNavigationModel } from "@/composables/useNavigationModel";
import { usePresenceStore } from "@/stores/PresenceStore";
import apiClient from "@/apiClient";

const auth = useAuthStore();
const route = useRoute();
const breadcrumbStore = useBreadcrumbStore();
const presenceStore = usePresenceStore();
const { currentArea, currentAreaItems, visibleCreateItems } = useNavigationModel();

const isDevelopmentEnvironment = computed(() => process.env.NODE_ENV !== "production");
const navClass = computed(() =>
  isDevelopmentEnvironment.value
    ? "p-4 border-b border-teal-700 bg-teal-600 text-white lg:sticky lg:top-0 lg:z-30"
    : "p-4 border-b border-slate-200 accent dark:border-slate-600 lg:sticky lg:top-0 lg:z-30",
);
const navContentClass = computed(() =>
  isDevelopmentEnvironment.value ? "text-white" : "accent",
);

defineProps({
  sidebarOpen: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(["toggle-sidebar"]);

const initials = computed(() => {
  const name = auth.name || auth.email || "";
  const parts = name.trim().split(" ").filter(Boolean);
  if (!parts.length) {
    return "U";
  }
  if (parts.length === 1) {
    return parts[0].slice(0, 2).toUpperCase();
  }
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
});

const avatarUrl = ref("");
const menuOpen = ref(false);
const menuRef = ref(null);
const createMenuOpen = ref(false);
const createMenuRef = ref(null);
const mobileSearchOpen = ref(false);
const mobileSearchRef = ref(null);
const presenceOpen = ref(false);
const presenceRef = ref(null);
const notificationsOpen = ref(false);
const notificationsRef = ref(null);
const notifications = ref([]);
const unreadNotificationCount = ref(0);
const notificationsLoading = ref(false);
let notificationIntervalId = null;

const avatarSrc = computed(() => auth.user?.avatarUrl || avatarUrl.value || "");
const hasPresenceAccess = computed(() => auth.isLoggedIn);
const hasNewsFeedAccess = computed(() => auth.hasRole("manager"));
const routeBreadcrumbs = computed(() =>
  Array.isArray(route.meta?.breadcrumbs) ? route.meta.breadcrumbs : [],
);
const namedRouteBreadcrumbs = computed(() =>
  routeBreadcrumbs.value.filter(
    (crumb) => typeof crumb?.name === "string" && crumb.name.trim().length > 0,
  ),
);
const mobilePageLabel = computed(() => {
  const explicitValue = String(breadcrumbStore.getValue ?? "").trim();
  if (explicitValue) {
    return explicitValue;
  }

  const lastNamedCrumb =
    namedRouteBreadcrumbs.value[namedRouteBreadcrumbs.value.length - 1]?.name;
  if (lastNamedCrumb) {
    return lastNamedCrumb;
  }

  return route.meta?.title || "Neptune";
});
const mobileSectionLabel = computed(() => {
  if (currentArea.value?.label) {
    return currentArea.value.label;
  }

  if (namedRouteBreadcrumbs.value.length > 1) {
    return namedRouteBreadcrumbs.value[0]?.name ?? "";
  }

  const title = String(route.meta?.title ?? "").trim();
  if (title && title !== mobilePageLabel.value) {
    return title;
  }

  return route.name === "calendar" ? "Neptune" : "";
});
const showMobileSectionLabel = computed(
  () =>
    Boolean(mobileSectionLabel.value) &&
    mobileSectionLabel.value !== mobilePageLabel.value,
);
const activeCount = computed(() => {
  const online = presenceStore.summary?.online ?? 0;
  const idle = presenceStore.summary?.idle ?? 0;
  return online + idle;
});
const activeEntries = computed(() => presenceStore.activeEntries.slice(0, 8));
const recentNotifications = computed(() => notifications.value.slice(0, 10));

const normalizePath = (path = "") => {
  const trimmed = String(path).replace(/\/+$/, "");
  return trimmed || "/";
};

const isCurrentAreaItem = (link = "") => {
  const current = normalizePath(route.path);
  const target = normalizePath(link);

  if (target === "/") {
    return current === "/";
  }

  return current === target || current.startsWith(`${target}/`);
};

const loadAvatar = async () => {
  try {
    const { data } = await apiClient.get("/api/user/avatar");
    avatarUrl.value = data?.image || "";
  } catch (error) {
    avatarUrl.value = "";
  }
};

const toggleMenu = () => {
  presenceOpen.value = false;
  notificationsOpen.value = false;
  mobileSearchOpen.value = false;
  createMenuOpen.value = false;
  menuOpen.value = !menuOpen.value;
};

const closeMenu = () => {
  menuOpen.value = false;
};

const toggleCreateMenu = () => {
  menuOpen.value = false;
  presenceOpen.value = false;
  notificationsOpen.value = false;
  mobileSearchOpen.value = false;
  createMenuOpen.value = !createMenuOpen.value;
};

const closeCreateMenu = () => {
  createMenuOpen.value = false;
};

const togglePresence = () => {
  menuOpen.value = false;
  createMenuOpen.value = false;
  mobileSearchOpen.value = false;
  notificationsOpen.value = false;
  presenceOpen.value = !presenceOpen.value;
  if (presenceOpen.value) {
    void presenceStore.load();
  }
};

const loadNotifications = async () => {
  if (!auth.isLoggedIn || notificationsLoading.value) {
    return;
  }

  notificationsLoading.value = true;
  try {
    const { data } = await apiClient.get("/api/notifications", {
      params: { limit: 10 },
      suppressAutoReload: true,
      suppressErrorAlert: true,
    });
    notifications.value = data?.data ?? [];
    unreadNotificationCount.value = data?.unread_count ?? 0;
  } catch {
    notifications.value = [];
    unreadNotificationCount.value = 0;
  } finally {
    notificationsLoading.value = false;
  }
};

const toggleNotifications = async () => {
  menuOpen.value = false;
  createMenuOpen.value = false;
  mobileSearchOpen.value = false;
  presenceOpen.value = false;
  notificationsOpen.value = !notificationsOpen.value;
  if (notificationsOpen.value) {
    await loadNotifications();
  }
};

const markNotificationRead = async (notification) => {
  if (!notification?.id || notification.read_at) {
    return;
  }

  await apiClient.put(`/api/notifications/${notification.id}/read`, {}, {
    suppressAutoReload: true,
    suppressErrorAlert: true,
  });
  await loadNotifications();
};

const markAllNotificationsRead = async () => {
  await apiClient.put("/api/notifications/read-all", {}, {
    suppressAutoReload: true,
    suppressErrorAlert: true,
  });
  await loadNotifications();
};

const toggleMobileSearch = async () => {
  presenceOpen.value = false;
  notificationsOpen.value = false;
  menuOpen.value = false;
  createMenuOpen.value = false;
  mobileSearchOpen.value = !mobileSearchOpen.value;

  if (mobileSearchOpen.value) {
    await nextTick();
    mobileSearchRef.value?.focusInput?.();
  }
};

const presenceDotClass = (status) => {
  if (status === "online") {
    return "bg-emerald-500";
  }
  if (status === "idle") {
    return "bg-amber-400";
  }
  return "bg-slate-300 dark:bg-slate-600";
};

const presenceBadgeClass = (status) => {
  if (status === "online") {
    return "bg-emerald-50 text-emerald-700 ring-emerald-200 dark:bg-emerald-500/10 dark:text-emerald-300 dark:ring-emerald-500/20";
  }
  if (status === "idle") {
    return "bg-amber-50 text-amber-700 ring-amber-200 dark:bg-amber-500/10 dark:text-amber-300 dark:ring-amber-500/20";
  }
  return "bg-slate-100 text-slate-600 ring-slate-200 dark:bg-slate-700 dark:text-slate-300 dark:ring-slate-600";
};

const presenceLabel = (entry) => {
  if (entry?.current_title) {
    return entry.current_title;
  }
  if (entry?.current_path) {
    return entry.current_path;
  }
  return "No current page";
};

const handleDocumentClick = (event) => {
  if (
    menuRef.value?.contains(event.target) ||
    presenceRef.value?.contains(event.target) ||
    notificationsRef.value?.contains(event.target) ||
    createMenuRef.value?.contains(event.target)
  ) {
    return;
  }
  closeMenu();
  closeCreateMenu();
  presenceOpen.value = false;
  notificationsOpen.value = false;
};

const handleLogout = async () => {
  await auth.logout();
  closeMenu();
  closeCreateMenu();
};

onMounted(() => {
  loadAvatar();
  loadNotifications();
  notificationIntervalId = window.setInterval(loadNotifications, 60000);
  document.addEventListener("click", handleDocumentClick);
  if (hasPresenceAccess.value) {
    presenceStore.startPolling();
  } else {
    presenceStore.reset();
  }
});

watch(
  () => auth.user?.avatarUrl,
  (val) => {
    avatarUrl.value = val || "";
  },
);

watch(
  () => route.fullPath,
  () => {
    closeMenu();
    closeCreateMenu();
    presenceOpen.value = false;
    notificationsOpen.value = false;
    mobileSearchOpen.value = false;
  },
);

watch(
  [() => route.fullPath, () => breadcrumbStore.getValue],
  () => {
    recordRecentNavigation({
      route,
      label: String(breadcrumbStore.getValue ?? "").trim(),
    });
  },
  { immediate: true },
);

onBeforeUnmount(() => {
  presenceStore.stopPolling();
  if (notificationIntervalId !== null) {
    window.clearInterval(notificationIntervalId);
  }
  document.removeEventListener("click", handleDocumentClick);
});
</script>
<template>
  <nav :class="navClass">
    <div class="flex flex-wrap items-center gap-3 md:gap-4">
      <div class="flex min-w-0 flex-1 items-center gap-2 md:gap-3">
        <button
          type="button"
          class="inline-flex items-center justify-center rounded-md border border-slate-200 bg-white/70 p-2 text-slate-700 shadow-sm hover:bg-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-cyan-500 dark:border-slate-600 dark:bg-slate-800 dark:text-slate-100 dark:hover:bg-slate-700 md:hidden"
          :aria-expanded="sidebarOpen"
          aria-label="Toggle navigation"
          @click="emit('toggle-sidebar')"
        >
          <Icon.Bars3Icon class="h-6 w-6" />
        </button>
        <router-link to="/calendar" aria-label="Go to calendar" class="shrink-0">
          <img src="/images/logo.png" alt="Neptune" class="h-10 w-auto object-contain" />
        </router-link>
        <div :class="navContentClass" class="min-w-0 flex-1">
          <div class="min-w-0 md:hidden">
            <p
              v-if="showMobileSectionLabel"
              class="truncate text-[10px] font-semibold uppercase tracking-[0.2em] opacity-70"
            >
              {{ mobileSectionLabel }}
            </p>
            <p class="truncate text-sm font-semibold">
              {{ mobilePageLabel }}
            </p>
          </div>
          <div class="hidden min-w-0 md:block">
            <BreadCrumb />
          </div>
        </div>
      </div>

      <div class="hidden min-w-0 flex-1 md:block md:min-w-[280px]">
        <SearchBar />
      </div>

      <div class="flex items-center gap-2">
        <div class="relative" ref="createMenuRef">
          <button
            v-if="visibleCreateItems.length"
            type="button"
            class="inline-flex items-center gap-2 rounded-full border border-cyan-200 bg-cyan-50 px-3 py-2 text-sm font-semibold text-cyan-700 shadow-sm hover:bg-cyan-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-cyan-500 dark:border-cyan-500/30 dark:bg-cyan-500/10 dark:text-cyan-100 dark:hover:bg-cyan-500/20"
            :aria-expanded="createMenuOpen"
            aria-haspopup="menu"
            aria-label="Open create menu"
            @click="toggleCreateMenu"
          >
            <Icon.PlusIcon class="h-4 w-4" />
            <span class="hidden md:inline">Create</span>
            <Icon.ChevronDownIcon class="h-4 w-4" />
          </button>
          <div
            v-if="createMenuOpen"
            class="absolute right-0 top-full z-40 mt-2 w-64 overflow-hidden rounded-xl border border-slate-200 bg-white shadow-lg dark:border-slate-700 dark:bg-slate-800"
            role="menu"
          >
            <div class="px-4 py-3">
              <p class="text-sm font-semibold text-slate-900 dark:text-slate-100">
                Create New
              </p>
              <p class="text-xs text-slate-500 dark:text-slate-400">
                Jump straight into common workflows.
              </p>
            </div>
            <div class="h-px bg-slate-200 dark:bg-slate-700"></div>
            <div class="py-2">
              <router-link
                v-for="item in visibleCreateItems"
                :key="item.to"
                :to="item.to"
                class="flex items-center gap-3 px-4 py-2 text-sm text-slate-700 hover:bg-slate-100 dark:text-slate-200 dark:hover:bg-slate-700"
                role="menuitem"
                @click="closeCreateMenu"
              >
                <component :is="item.icon" class="h-4 w-4 text-cyan-600 dark:text-cyan-300" />
                <span>{{ item.label }}</span>
              </router-link>
            </div>
          </div>
        </div>

        <button
          type="button"
          class="inline-flex items-center justify-center rounded-full border border-slate-200 bg-white/80 p-2 text-slate-700 shadow-sm hover:bg-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-cyan-500 dark:border-slate-600 dark:bg-slate-800 dark:text-slate-100 dark:hover:bg-slate-700 md:hidden"
          :aria-expanded="mobileSearchOpen"
          aria-label="Open search"
          @click="toggleMobileSearch"
        >
          <Icon.MagnifyingGlassIcon class="h-5 w-5" />
        </button>

        <router-link
          v-if="hasNewsFeedAccess"
          to="/manager/news"
          class="relative inline-flex items-center justify-center rounded-full border border-slate-200 bg-white p-2 text-slate-700 shadow-sm hover:bg-slate-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-cyan-500 dark:border-slate-600 dark:bg-slate-800 dark:text-slate-100 dark:hover:bg-slate-700"
          :class="route.name === 'news-feed' ? 'text-cyan-600 dark:text-cyan-300' : ''"
          aria-label="Open news feed"
          title="News Feed"
        >
          <Icon.NewspaperIcon class="h-5 w-5" />
        </router-link>

        <div class="relative" ref="notificationsRef">
          <button
            type="button"
            class="relative inline-flex items-center justify-center rounded-full border border-slate-200 bg-white p-2 text-slate-700 shadow-sm hover:bg-slate-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-cyan-500 dark:border-slate-600 dark:bg-slate-800 dark:text-slate-100 dark:hover:bg-slate-700"
            :aria-expanded="notificationsOpen"
            aria-haspopup="menu"
            aria-label="Open notifications"
            @click="toggleNotifications"
          >
            <Icon.BellIcon class="h-5 w-5" />
            <span
              v-if="unreadNotificationCount > 0"
              class="absolute -right-1 -top-1 flex min-h-5 min-w-5 items-center justify-center rounded-full bg-rose-600 px-1 text-[10px] font-semibold text-white ring-2 ring-white dark:ring-slate-800"
            >
              {{ unreadNotificationCount > 9 ? '9+' : unreadNotificationCount }}
            </span>
          </button>
          <div
            v-if="notificationsOpen"
            class="absolute right-0 top-full z-40 mt-2 w-[calc(100vw-2rem)] max-w-96 overflow-hidden rounded-xl border border-slate-200 bg-white shadow-lg dark:border-slate-700 dark:bg-slate-800"
            role="menu"
          >
            <div class="flex items-center justify-between gap-3 px-4 py-3">
              <div>
                <p class="text-sm font-semibold text-slate-900 dark:text-slate-100">Notifications</p>
                <p class="text-xs text-slate-500 dark:text-slate-400">
                  {{ unreadNotificationCount }} unread
                </p>
              </div>
              <button
                type="button"
                class="text-xs font-medium text-cyan-600 hover:text-cyan-700 disabled:opacity-50 dark:text-cyan-400 dark:hover:text-cyan-300"
                :disabled="unreadNotificationCount < 1"
                @click="markAllNotificationsRead"
              >
                Mark all read
              </button>
            </div>
            <div class="h-px bg-slate-200 dark:bg-slate-700"></div>
            <div v-if="notificationsLoading && !recentNotifications.length" class="px-4 py-4 text-sm text-slate-500 dark:text-slate-300">
              Loading notifications...
            </div>
            <div v-else-if="!recentNotifications.length" class="px-4 py-4 text-sm text-slate-500 dark:text-slate-300">
              No notifications.
            </div>
            <ul v-else class="max-h-96 divide-y divide-slate-200 overflow-y-auto dark:divide-slate-700">
              <li
                v-for="notification in recentNotifications"
                :key="notification.id"
                class="px-4 py-3"
                :class="notification.read_at ? '' : 'bg-cyan-50/70 dark:bg-cyan-950/20'"
              >
                <div class="flex items-start gap-3">
                  <div class="mt-1 h-2.5 w-2.5 shrink-0 rounded-full" :class="notification.read_at ? 'bg-slate-300 dark:bg-slate-600' : 'bg-cyan-500'"></div>
                  <div class="min-w-0 flex-1">
                    <router-link
                      v-if="notification.data?.url"
                      :to="notification.data.url"
                      class="block text-sm font-semibold text-slate-900 hover:text-cyan-700 dark:text-slate-100 dark:hover:text-cyan-300"
                      @click="markNotificationRead(notification)"
                    >
                      {{ notification.data?.title || 'Notification' }}
                    </router-link>
                    <p v-else class="text-sm font-semibold text-slate-900 dark:text-slate-100">
                      {{ notification.data?.title || 'Notification' }}
                    </p>
                    <p class="mt-1 text-sm leading-5 text-slate-600 dark:text-slate-300">
                      {{ notification.data?.body || '' }}
                    </p>
                    <div class="mt-2 flex items-center justify-between gap-3 text-xs text-slate-400">
                      <span><TimeAgo :data="notification.created_at" /></span>
                      <button
                        v-if="!notification.read_at"
                        type="button"
                        class="font-medium text-cyan-600 hover:text-cyan-700 dark:text-cyan-400 dark:hover:text-cyan-300"
                        @click="markNotificationRead(notification)"
                      >
                        Mark read
                      </button>
                    </div>
                  </div>
                </div>
              </li>
            </ul>
          </div>
        </div>

        <div v-if="hasPresenceAccess" class="relative hidden md:block" ref="presenceRef">
          <button
            type="button"
            class="inline-flex items-center gap-2 rounded-full border border-slate-200 bg-white px-3 py-2 text-sm font-medium text-slate-700 shadow-sm hover:bg-slate-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-cyan-500 dark:border-slate-600 dark:bg-slate-800 dark:text-slate-100 dark:hover:bg-slate-700"
            :aria-expanded="presenceOpen"
            aria-haspopup="menu"
            aria-label="Open online team panel"
            @click="togglePresence"
          >
            <span class="relative flex h-2.5 w-2.5">
              <span class="absolute inset-0 rounded-full bg-emerald-400/40" :class="{ 'animate-ping': activeCount > 0 }"></span>
              <span class="relative h-2.5 w-2.5 rounded-full bg-emerald-500"></span>
            </span>
            <span>Team {{ activeCount }}</span>
          </button>
          <div
            v-if="presenceOpen"
            class="absolute right-0 top-full z-40 mt-2 w-[calc(100vw-2rem)] max-w-80 rounded-xl border border-slate-200 bg-white shadow-lg dark:border-slate-700 dark:bg-slate-800"
            role="menu"
          >
            <div class="flex items-center justify-between px-4 py-3">
              <div>
                <p class="text-sm font-semibold text-slate-900 dark:text-slate-100">Online Team</p>
                <p class="text-xs text-slate-500 dark:text-slate-400">
                  {{ presenceStore.summary.online }} online, {{ presenceStore.summary.idle }} idle
                </p>
              </div>
              <button
                type="button"
                class="text-xs font-medium text-cyan-600 hover:text-cyan-700 dark:text-cyan-400 dark:hover:text-cyan-300"
                @click="presenceStore.load"
              >
                Refresh
              </button>
            </div>
            <div class="h-px bg-slate-200 dark:bg-slate-700"></div>
            <div v-if="presenceStore.loading && !presenceStore.loaded" class="px-4 py-4 text-sm text-slate-500 dark:text-slate-300">
              Loading presence...
            </div>
            <div v-else-if="!activeEntries.length" class="px-4 py-4 text-sm text-slate-500 dark:text-slate-300">
              No recent online users.
            </div>
            <ul v-else class="divide-y divide-slate-200 dark:divide-slate-700">
              <li v-for="entry in activeEntries" :key="entry.id" class="px-4 py-3">
                <div class="flex items-start gap-3">
                  <div
                    class="flex h-9 w-9 shrink-0 items-center justify-center overflow-hidden rounded-full bg-slate-100 text-xs font-semibold text-slate-600 dark:bg-slate-700 dark:text-slate-200"
                  >
                    <img v-if="entry.avatar_url" :src="entry.avatar_url" :alt="entry.name" class="h-full w-full object-cover" />
                    <span v-else>{{ entry.name?.[0] ?? "?" }}</span>
                  </div>
                  <div class="min-w-0 flex-1">
                    <div class="flex flex-wrap items-center gap-2">
                      <span class="text-sm font-semibold text-slate-900 dark:text-slate-100">{{ entry.name }}</span>
                      <span
                        class="inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-medium ring-1 ring-inset"
                        :class="presenceBadgeClass(entry.status)"
                      >
                        <span class="h-2 w-2 rounded-full" :class="presenceDotClass(entry.status)"></span>
                        {{ entry.status }}
                      </span>
                    </div>
                    <div class="truncate text-xs text-slate-500 dark:text-slate-400">
                      {{ presenceLabel(entry) }}
                    </div>
                    <div class="mt-1 flex items-center gap-1 text-xs text-slate-400">
                      <span>Seen</span>
                      <TimeAgo :data="entry.last_seen_at" />
                    </div>
                  </div>
                </div>
              </li>
            </ul>
          </div>
        </div>

        <div class="relative flex items-center gap-2" ref="menuRef">
          <button
            type="button"
            class="flex h-9 w-9 items-center justify-center overflow-hidden rounded-full bg-slate-200 text-xs font-semibold text-slate-600 ring-2 ring-white shadow-sm dark:bg-slate-700 dark:text-slate-100 dark:ring-slate-700"
            :aria-expanded="menuOpen"
            aria-haspopup="menu"
            aria-label="Open user menu"
            @click="toggleMenu"
          >
            <img v-if="avatarSrc" :src="avatarSrc" alt="Avatar" class="h-full w-full object-cover object-center" />
            <span v-else>{{ initials }}</span>
          </button>
          <div
            v-if="menuOpen"
            class="absolute right-0 top-full z-40 mt-2 w-52 overflow-hidden rounded-xl border border-slate-200 bg-white shadow-lg dark:border-slate-700 dark:bg-slate-800"
            role="menu"
          >
            <div class="px-4 py-3">
              <p class="text-sm font-semibold text-slate-900 dark:text-slate-100">
                {{ auth.name || "Account" }}
              </p>
              <p class="text-xs text-slate-500 dark:text-slate-400">
                {{ auth.email || "Signed in" }}
              </p>
            </div>
            <div class="h-px bg-slate-200 dark:bg-slate-700"></div>
            <div class="py-2">
              <router-link
                to="/settings"
                class="flex w-full items-center px-4 py-2 text-sm text-slate-700 hover:bg-slate-100 dark:text-slate-200 dark:hover:bg-slate-700"
                role="menuitem"
                @click="closeMenu"
              >
                Settings
              </router-link>
            </div>
            <div class="h-px bg-slate-200 dark:bg-slate-700"></div>
            <div class="py-2">
              <button
                type="button"
                class="flex w-full items-center px-4 py-2 text-sm text-rose-600 hover:bg-rose-50 dark:text-rose-300 dark:hover:bg-rose-500/10"
                role="menuitem"
                @click="handleLogout"
              >
                Log out
              </button>
            </div>
          </div>
        </div>
      </div>

      <div v-if="mobileSearchOpen" class="w-full md:hidden">
        <div class="rounded-2xl border border-slate-200 bg-white/95 p-3 shadow-sm dark:border-slate-700 dark:bg-slate-800/95">
          <div class="mb-3 flex items-start justify-between gap-3">
            <div>
              <p class="text-xs font-semibold uppercase tracking-[0.18em] text-slate-500">
                Search
              </p>
              <p class="text-xs text-slate-500 dark:text-slate-400">
                Find customers, jobs, tickets and more.
              </p>
            </div>
            <button
              type="button"
              class="inline-flex items-center justify-center rounded-full border border-slate-200 bg-white/80 p-2 text-slate-600 shadow-sm hover:bg-white dark:border-slate-600 dark:bg-slate-700 dark:text-slate-200 dark:hover:bg-slate-600"
              aria-label="Close search"
              @click="mobileSearchOpen = false"
            >
              <Icon.XMarkIcon class="h-4 w-4" />
            </button>
          </div>
          <SearchBar ref="mobileSearchRef" />
        </div>
      </div>

      <div
        v-if="currentArea && currentAreaItems.length"
        class="w-full overflow-x-auto border-t border-white/20 pt-2 md:pt-1"
      >
        <div class="flex min-w-max items-center gap-2">
          <span
            class="hidden rounded-full bg-white/15 px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.18em] text-white/80 md:inline-flex"
          >
            {{ currentArea.label }}
          </span>
          <router-link
            v-for="item in currentAreaItems"
            :key="item.link"
            :to="item.link"
            class="rounded-full border px-3 py-1.5 text-sm font-medium transition-colors"
            :class="isCurrentAreaItem(item.link)
              ? 'border-white/50 bg-white text-slate-900'
              : 'border-white/20 bg-white/10 text-white hover:bg-white/20'"
          >
            {{ item.name }}
          </router-link>
        </div>
      </div>
    </div>
  </nav>
</template>
